import React from "react";
import { Component } from "react";
import { Form, Button } from "semantic-ui-react";
import moment from "moment";
import { ReactCookieProps, withCookies } from "react-cookie";
import base64 from "base-64";
import utf8 from "utf8";

import NumberFormat from "react-number-format";
import PhoneNumber from "awesome-phonenumber";
import PhoneInput, { CountryData } from "react-phone-input-2";
import {
    ObjectLiteral,
    parentDataRequiredToRegister,
    minYearsToRegister,
    customPhoneMasks,
} from "../../constants";
import * as Sentry from "@sentry/nextjs";
import _ from "lodash";
import { Props, registrationType } from "../propTypes";
import { childFormComponent } from "./types";
import { ProgressIndicator } from "./ProgressIndicator";
import classnames from "classnames";
import { ResetForm } from "./ResetForm";

// Personal Details Form Component
/**
 * This component is used to input and store a user's personal details in MyElevation.
 *
 * We are using the Form and Button components from Semantic UI React Library.
 * For more information on these components can be found at https://react.semantic-ui.com/collections/form/
 * and https://react.semantic-ui.com/elements/button/
 *
 *
 * For phone numbers, we are using the PhoneInput component for international phone number formatting
 * (https://www.npmjs.com/package/react-phone-input-2) and the PhoneNumber component to validate
 * (https://www.npmjs.com/package/awesome-phonenumber)
 *
 * React Cookie is being used to set a cookie on the user's browser for 30 minutes. More information about this
 * can be found at https://www.npmjs.com/package/react-cookie/
 *
 * moment.js is used to format the selected start date. For more information, check the documentation
 * at https://momentjs.com/docs/.
 */

type State = {
    validEmail: boolean;
    validPhone: boolean;
    validDOB: boolean;
    isMinYearsToRegister: boolean;
    showValidation: boolean;
    validationText: string;
    phoneFormatData: ObjectLiteral;
    phoneInputContent: string;
};

class PersonalDetails extends Component<
    Props & childFormComponent & ReactCookieProps,
    State
> {
    state = {
        validEmail: false,
        validPhone: false,
        validDOB: false,
        isMinYearsToRegister: false,
        showValidation: false,
        validationText: "",
        phoneFormatData: {} as ObjectLiteral,
        phoneInputContent: "+1",
    };

    showErrorTimeout = null;

    // Component Did Update Lifecycle Method
    /**
     * componentDidUpdate() runs immediately after the component updates.
     * This method does not run during the initial render.
     *
     * In this instance, we are validating the code entered by the user. If there is an error, we return a message.
     * If valid, we create a cookie for 30 minutes, so that if the user goes to the form again, they can skip this step.
     */
    componentDidUpdate(
        prevProps: Props & childFormComponent & ReactCookieProps
    ) {
        if (
            this.props.main.searchContactData &&
            this.props.main.searchContactData.status_code &&
            this.props.main.searchContactData.status_code !== 200 &&
            this.props.main.searchContactData.status_code !== 409 &&
            this.props.main.searchContactData.status_code !== 404 &&
            prevProps.main.searchContactData &&
            prevProps.main.searchContactData.status_code !==
                this.props.main.searchContactData.status_code
        ) {
            if (this.props.cookies) {
                this.props.cookies.remove(
                    "confirmation_" + this.props.registrationType.toLowerCase(),
                    { path: "/" }
                );
            }

            let errorText = "Error sending data. Please try again later.";
            if (
                this.props.main.searchContactData.message?.[0]?.email ===
                "The email must be a valid email address."
            ) {
                errorText =
                    "Error 001. Please contact data@elevationchurch.org for assistance.";
            } else if (
                this.props.main.searchContactData.message &&
                _.isArray(this.props.main.searchContactData.message) &&
                this.props.main.searchContactData.message[0] &&
                _.isObject(this.props.main.searchContactData.message[0])
            ) {
                errorText =
                    this.props.main.searchContactData &&
                    this.props.main.searchContactData.messages &&
                    (
                        this.props.main.searchContactData
                            .message as ObjectLiteral[]
                    )[0][
                        Object.keys(
                            this.props.main.searchContactData.message[0]
                        )[0]
                    ];
            }

            const extraErrorData = {
                status_code: this.props.main.searchContactData.status_code,
                other_validation_data: this.props.main.searchContactData,
            };

            if (this.props.main.searchContactData.origin !== "USER") {
                this.props.logError &&
                    this.props.logError(
                        errorText,
                        Sentry.Severity.Fatal,
                        extraErrorData
                    );
            }

            this.setState({
                showValidation: true,
                validationText: errorText,
            });
        }

        if (
            this.props.main.searchContactData &&
            (this.props.main.searchContactData.status_code === 200 ||
                this.props.main.searchContactData.status_code === 409 ||
                this.props.main.searchContactData.status_code === 404) &&
            prevProps.main.searchContactData &&
            prevProps.main.searchContactData.status_code !==
                this.props.main.searchContactData.status_code
        ) {
            const { cookies, values } = this.props;

            if (
                this.props.main.searchContactData.data &&
                this.props.main.searchContactData.data &&
                this.props.main.searchContactData.data.length === 1
            ) {
                let confirmationCodeCookieMaxAge = 1800; // 30 minutes
                let text = "";
                if (values.emailConfirmation) {
                    text = `${JSON.stringify({
                        type: "email",
                        data: { email: values.emailConfirmation },
                    })}+++`;
                } else {
                    text = `${JSON.stringify({
                        type: "phone",
                        data: {
                            phoneNumber: values.phoneConfirmation,
                            extraData: values.phoneConfirmationFormat,
                        },
                    })}+++`;
                }

                if (
                    this.props.main.searchContactData.data[0] &&
                    values.phoneConfirmation
                ) {
                    text = `${text}${JSON.stringify({
                        ...this.props.main.searchContactData.data[0],
                        fromSalesForce: true,
                    })}`;
                } else if (
                    this.props.main.searchContactData.data[0] &&
                    values.emailConfirmation
                ) {
                    text = `${text}${JSON.stringify({
                        ...this.props.main.searchContactData.data[0],
                        fromSalesForce: true,
                    })}`;
                }

                const bytes = utf8.encode(text);
                const content = base64.encode(bytes);
                const userData = this.props.main.searchContactData.data[0];

                if (this.props.saveSession && cookies) {
                    cookies.set(
                        "confirmation_" +
                            this.props.registrationType.toLowerCase(),
                        content,
                        { path: "/", maxAge: confirmationCodeCookieMaxAge }
                    );
                }

                this.props.handleChange("fromSalesForce")(null, true);
                if (parentDataRequiredToRegister(moment(userData.birthdate))) {
                    this.props.nextStep(12);
                } else if (
                    this.props.allowContactToUpdateSalesForceInfo &&
                    this.props.isContactAllowedToUpdateSalesForceInfo &&
                    this.props.isContactAllowedToUpdateSalesForceInfo(
                        userData
                    ) &&
                    ((values.emailConfirmation &&
                        values.emailConfirmation !== userData.email) ||
                        (values.phoneConfirmation &&
                            values.phoneConfirmation !== userData.phone))
                ) {
                    this.props.nextStep(14);
                } else {
                    this.props.nextStep(6);
                }
            } else {
                if (this.props.disableSearchAndCreate) {
                    this.props.nextStep(6);
                } else {
                    this.props.nextStep();
                }
            }
        }

        if (
            this.props.main.token_error &&
            this.state.validationText !== this.props.main.token_error
        ) {
            this.setState({
                showValidation: true,
                validationText: this.props.main.token_error,
            });
        }
    }

    // Data To Send Function
    /**
     * dataToSend() stores the values of the user's information based on the type of confirmation used.
     */
    dataToSend = () => {
        const { values } = this.props;

        if (
            this.props.customPersonaDetailsDataToSendCallback &&
            _.isFunction(this.props.customPersonaDetailsDataToSendCallback)
        ) {
            const phoneNumberFromForm = values.phone.replace(/\D/g, "");
            const mobileNumberFromForm = phoneNumberFromForm.replace(
                this.state.phoneFormatData.dialCode,
                ""
            );
            const mobileDialingCodeFromForm =
                this.state.phoneFormatData.dialCode;

            return this.props.customPersonaDetailsDataToSendCallback(
                values,
                mobileDialingCodeFromForm,
                mobileNumberFromForm
            );
        }

        const dataToSend: ObjectLiteral = {
            first_name: values.firstName,
            last_name: values.lastName,
        };

        if (values.phoneConfirmation) {
            const phoneNumber = values.phoneConfirmation.replace(/\D/g, "");

            dataToSend["mobile_number"] = phoneNumber.replace(
                values.phoneConfirmationFormat.dialCode,
                ""
            );
            dataToSend["mobile_dialing_code"] =
                values.phoneConfirmationFormat.dialCode;
            dataToSend["email"] = values.email;
        }

        if (values.emailConfirmation) {
            const phoneNumber = values.phone.replace(/\D/g, "");
            dataToSend["email"] = values.emailConfirmation;

            if (
                this.state.phoneFormatData &&
                this.state.phoneFormatData.dialCode
            ) {
                dataToSend["mobile_number"] = phoneNumber.replace(
                    this.state.phoneFormatData.dialCode,
                    ""
                );
                dataToSend["mobile_dialing_code"] =
                    this.state.phoneFormatData.dialCode;
            }
        }

        return dataToSend;
    };

    // Save and Continue Email & Name Function
    /**
     * saveAndContinueEmailName() stores the values of the user's information based on the type of confirmation used,
     * and returns errors, if needed.
     */
    saveAndContinueEmailName = (e: any) => {
        e.preventDefault();
        const { values } = this.props;

        if (
            (values.phoneConfirmation && this.state.validEmail) ||
            (values.emailConfirmation && this.state.validPhone)
        ) {
            const dataToSend = {
                ...this.dataToSend(),
            };

            if (values.emailConfirmation && this.state.validPhone) {
                this.props.handleChange("phoneFormat")(
                    null,
                    this.state.phoneFormatData
                );
            }

            this.props.actions.main.main_search_contact({
                dataToSearch: dataToSend,
            });
        } else if (values.emailConfirmation && !this.state.validPhone) {
            this.setState({
                showValidation: true,
                validationText: "Please enter a valid phone number",
            });
        } else if (values.phoneConfirmation && !this.state.validEmail) {
            this.setState({
                showValidation: true,
                validationText: "Please enter a valid email",
            });
        }
    };

    // Save and Continue DOB Function
    /**
     * saveAndContinueDOB() stores the values of the user's information based on the type of confirmation used,
     * and returns errors, if needed.
     */
    saveAndContinueDOB = (e: any) => {
        e.preventDefault();
        const { values } = this.props;

        if (this.state.validDOB) {
            const dataToSend = {
                ...this.dataToSend(),

                birthdate: moment(
                    new Date(values.birthday),
                    "MM/DD/YYYY"
                ).format("YYYY-MM-DD"),
                showValidation: false,
                validationText: "",
            };

            this.props.actions.main.main_search_contact({
                dataToSearch: dataToSend,
            });
        } else {
            this.setState({
                showValidation: true,
                validationText: this.state.isMinYearsToRegister
                    ? `You have to be ${minYearsToRegister} or older to sign up`
                    : `Please enter a valid birthdate.`,
            });
        }
    };

    // Handle Email Change Function
    /**
     * handleEmailChange() handles the value of the email input and checks to make sure it's valid.
     */
    handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const regex = RegExp(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i);
        const valid = regex.test(e.target.value);

        if (valid !== this.state.validEmail) {
            this.setState({
                validEmail: valid,
                showValidation: false,
                validationText: "",
            });
        }

        this.props.handleChange("email")(e);
    };

    // Handle DOB Change Function
    /**
     * handleDOBChange() handles the value of the dob input and checks to make sure it's valid.
     */
    handleDOBChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const date = moment(e.target.value, "MM/DD/YYYY", true);
        const valid =
            date.isValid() &&
            date.isBefore(moment().subtract(minYearsToRegister, "years")) &&
            date.isAfter(moment().subtract(120, "years"));

        if (valid !== this.state.validDOB) {
            clearTimeout(this.showErrorTimeout);
            this.setState({
                validDOB: valid,
                isMinYearsToRegister: date.isValid()
                    ? date.isBefore(
                          moment().subtract(minYearsToRegister, "years")
                      )
                    : false,
                showValidation: false,
                validationText: "",
            });
        } else {
            if (this.showErrorTimeout) {
                clearTimeout(this.showErrorTimeout);

                this.setState({
                    showValidation: false,
                });
            }

            this.showErrorTimeout = setTimeout(() => {
                this.setState({
                    showValidation: true,
                    validationText:
                        date.isValid() &&
                        !date.isBefore(
                            moment().subtract(minYearsToRegister, "years")
                        )
                            ? `You have to be ${minYearsToRegister} or older to sign up`
                            : `Please enter a valid birthdate.`,
                });
            }, 500);
        }

        this.props.handleChange("birthday")(e);
    };

    // Handle Phone Change Function
    /**
     * handlePhoneChange() handles the value of the phone input and checks to make sure it's valid.
     */
    handlePhoneChange = (
        telNumber: any,
        data: CountryData,
        e,
        formattedPhoneNumber
    ) => {
        const pn = new PhoneNumber(formattedPhoneNumber);
        const valid = pn.isValid() || false;
        if (valid !== this.state.validPhone) {
            this.setState({
                validPhone: valid,
                phoneFormatData: data,
                showValidation: false,
                validationText: "",
                phoneInputContent: telNumber,
            });
        } else {
            this.setState({
                phoneInputContent: telNumber,
            });
        }

        this.props.handleChange("phone")(null, telNumber);
    };

    capitalize = (s: string) => {
        if (typeof s !== "string") return "";
        return s.charAt(0).toUpperCase() + s.slice(1);
    };

    subHeaderContent = () => {
        let text = "";

        if (this.props.registrationType === registrationType.eGroup) {
            text = "Let your eGroup leader know who you are.";
        }

        if (
            this.props.registrationType === registrationType.eTeam ||
            this.props.registrationType === registrationType.Baptism ||
            this.props.registrationType === registrationType.event
        ) {
            text = "Let your campus staff know who you are.";
        }

        return text;
    };

    // Render Content Function
    /**
     * renderNameEmailPhone() displays the content after the view has loaded, based on values based on confirmation type.
     */
    renderNameEmailPhone = () => {
        const { values } = this.props;
        const pn = new PhoneNumber(
            values.phoneConfirmation.replace(
                values.phoneConfirmationFormat.dialCode,
                ""
            ),
            values.phoneConfirmationFormat.countryCode
        );
        const validPhoneConfirmation = pn.isValid() || false;
        return (
            <>
                <div className="col-xs-12 max-w-500 full-width center-content m-b-spacer-3xl">
                    <h2 className="text-dark text-left m-b-spacer-none text-center">
                        What&#39;s your Name &{" "}
                        {`${validPhoneConfirmation ? "Email" : "Phone"}`}?
                    </h2>
                    <p className="text-dark half-here text-center">
                        {this.subHeaderContent()}
                    </p>

                    <Form
                        className={`p-y-spacer-xl phone-number-form ${
                            this.state.showValidation ? "ec-error" : ""
                        }`}
                    >
                        <Form.Field
                            className={classnames(
                                "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg",
                                {
                                    filled: values.firstName,
                                }
                            )}
                        >
                            <label>First Name</label>
                            <input
                                className=" p-x-spacer-lg no-border ec-border-radius-sm b-width-xs border-trans-light-grey-alt text-alt-dark-grey full-width"
                                onChange={this.props.handleChange("firstName")}
                                defaultValue={values.firstName}
                                autoFocus={true}
                                maxLength={50}
                            />
                        </Form.Field>
                        <Form.Field
                            className={classnames(
                                "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg ",
                                {
                                    filled: values.lastName,
                                }
                            )}
                        >
                            <label>Last Name</label>
                            <input
                                className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                onChange={this.props.handleChange("lastName")}
                                defaultValue={values.lastName}
                                maxLength={50}
                            />
                        </Form.Field>

                        {values.phoneConfirmation && validPhoneConfirmation ? (
                            <Form.Field
                                className={classnames(
                                    "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg ",
                                    {
                                        filled: values.email,
                                    }
                                )}
                            >
                                <label>Email Address</label>
                                <input
                                    type="email"
                                    className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                    onChange={this.handleEmailChange}
                                    value={values.email}
                                />

                                {this.state.showValidation ? (
                                    <small className="ec-primary-font ec-line-height">
                                        {this.state.validationText}
                                    </small>
                                ) : null}
                            </Form.Field>
                        ) : (
                            <Form.Field
                                className={classnames(
                                    "label-animation-input with-phone-field rel-pos flex flex-column full-width m-b-spacer-lg ",
                                    {
                                        filled: this.state.phoneInputContent,
                                    }
                                )}
                            >
                                <label>Phone Number</label>
                                <PhoneInput
                                    country="us"
                                    preferredCountries={["us"]}
                                    value={values.phone}
                                    inputClass={`full-width`}
                                    inputProps={{
                                        autoFocus: false,
                                        autoComplete: "off",
                                    }}
                                    placeholder=""
                                    onChange={this.handlePhoneChange}
                                    disabled={validPhoneConfirmation}
                                    masks={customPhoneMasks}
                                />
                                {this.state.showValidation ? (
                                    <small className="ec-primary-font ec-line-height">
                                        {this.state.validationText}
                                    </small>
                                ) : null}
                            </Form.Field>
                        )}

                        {this.props.isLoading ? (
                            <div className="form-loading rel-pos  m-t-spacer-md  way-forward full-width uptop">
                                <div className="la-ball-clip-rotate la-color la-md loading-spin abs-pos absolute-center">
                                    <div />
                                </div>
                            </div>
                        ) : (
                            <Button
                                id={`${this.props.tagPrefixId}-personal-info-next`}
                                disabled={
                                    !values.firstName ||
                                    !values.lastName ||
                                    !!(
                                        validPhoneConfirmation && !values.email
                                    ) ||
                                    !!(
                                        values.emailConfirmation &&
                                        !this.state.validPhone
                                    ) ||
                                    !!(
                                        !validPhoneConfirmation &&
                                        !values.emailConfirmation
                                    )
                                }
                                className="full-width no-border text-white  p-y-spacer-md uppercase let-sp-reg hover-grow ec-border-radius-sm"
                                onClick={this.saveAndContinueEmailName}
                            >
                                Next
                            </Button>
                        )}
                    </Form>
                </div>
                <ResetForm
                    idPrefix={`${this.props.tagPrefixId}-personal-info`}
                    cancelUrl={this.props.cancelUrl || ""}
                />
            </>
        );
    };

    // Render Content Function
    /**
     * renderDOB() displays the content after the view has loaded.
     */

    renderDOB = () => {
        const { values } = this.props;

        return (
            <>
                <div className="col-xs-12 max-w-500 full-width center-content m-b-spacer-3xl">
                    <h2
                        className={classnames(
                            "text-dark text-left text-center",
                            {
                                "m-b-spacer-md":
                                    this.props.registrationType !== "eTeam",
                                "m-b-spacer-none":
                                    this.props.registrationType === "eTeam",
                            }
                        )}
                    >
                        What&#39;s your Birthday?
                    </h2>
                    {this.props.registrationType === "eTeam" ? (
                        <p className="text-dark half-here text-center m-b-spacer-lg">
                            You must be 16 or older to join
                        </p>
                    ) : null}

                    <Form
                        className={`p-b-spacer-xl  ${
                            this.state.showValidation ? "ec-error" : ""
                        }`}
                    >
                        <Form.Field
                            className={classnames(
                                "label-animation-input rel-pos flex flex-column full-width m-b-spacer-lg ",
                                {
                                    filled: values.birthday,
                                }
                            )}
                        >
                            <label>Birthday</label>
                            <NumberFormat
                                autoFocus={true}
                                format="##/##/####"
                                placeholder="MM/DD/YYYY"
                                value={values.birthday}
                                className="p-x-spacer-lg no-border ec-border-radius-sm b-width-xs  border-trans-light-grey-alt text-alt-dark-grey full-width"
                                onChange={this.handleDOBChange}
                                alwaysShowMask={false}
                            />
                            {this.state.showValidation ? (
                                <small className="ec-primary-font ec-line-height">
                                    {this.state.validationText}
                                </small>
                            ) : null}
                        </Form.Field>

                        {this.props.isLoading ? (
                            <div className="form-loading rel-pos  m-t-spacer-md  way-forward full-width uptop">
                                <div className="la-ball-clip-rotate la-color la-md loading-spin abs-pos absolute-center">
                                    <div />
                                </div>
                            </div>
                        ) : (
                            <Button
                                id={`${this.props.tagPrefixId}-birthday-next`}
                                disabled={
                                    !values.birthday || !this.state.validDOB
                                }
                                className="full-width no-border text-white  m-t-spacer-md p-y-spacer-md uppercase let-sp-reg hover-grow ec-border-radius-sm"
                                onClick={this.saveAndContinueDOB}
                            >
                                Next
                            </Button>
                        )}
                    </Form>
                    <ProgressIndicator currentStep={values.step} />
                </div>
                <ResetForm
                    idPrefix={`${this.props.tagPrefixId}-birthday`}
                    cancelUrl={this.props.cancelUrl || ""}
                />
            </>
        );
    };

    render() {
        if (this.props.step === "name-email") {
            return this.renderNameEmailPhone();
        }

        if (this.props.step === "DOB") {
            return this.renderDOB();
        }

        return null;
    }
}

export default withCookies(PersonalDetails);
